import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";

function formatError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AuthPage() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      let userData;
      if (mode === "login") {
        userData = await login(form.email, form.password);
      } else {
        userData = await register(form.name, form.email, form.password, form.phone);
      }
      navigate(userData?.role === "admin" ? "/admin" : "/");
    } catch (err) {
      if (!err.response) {
        setError("Cannot reach the server. Make sure the backend is running on port 8000.");
      } else if (err.response.status === 503) {
        setError("Database is temporarily unavailable. Check MongoDB Atlas network access and try again in a minute.");
      } else if (err.response.status === 404) {
        setError("Auth API not found (404). Restart the frontend dev server and check backend URL settings.");
      } else {
        setError(formatError(err.response?.data?.detail) || err.message);
      }
    } finally { setLoading(false); }
  };

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  return (
    <div className="min-h-screen bg-petal-beige flex" data-testid="auth-page">
      {/* Left side - Brand */}
      <div className="hidden lg:flex lg:w-1/2 bg-petal-olive-text flex-col items-center justify-center p-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src="https://images.unsplash.com/photo-1606229365485-93a3b8ee0385?w=800&q=60" alt=""
            className="w-full h-full object-cover" />
        </div>
        <div className="relative z-10 text-center">
          <div className="flex items-center justify-center mb-8">
            <Logo className="h-20" link={false} />
          </div>
          <p className="font-dancing text-petal-gold text-3xl mb-6">Made with love,<br />wrapped with happiness</p>
          <p className="font-outfit text-petal-border/80 text-sm max-w-xs leading-relaxed">
            Handcrafted gifts for every special occasion. Join our family of happy customers and let us create something beautiful for you.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-6">
            {["500+ Happy Customers", "100% Handmade", "Premium Quality"].map(s => (
              <div key={s} className="border border-petal-gold/30 p-4">
                <p className="font-outfit text-xs text-petal-border/70 text-center">{s}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <Logo className="h-14" link={false} />
          </div>

          {/* Tabs */}
          <div className="flex border border-petal-border mb-8">
            {["login","register"].map(m => (
              <button key={m} onClick={() => { setMode(m); setError(""); }}
                data-testid={`auth-tab-${m}`}
                className={`flex-1 py-3 font-outfit text-sm uppercase tracking-widest transition-colors ${mode === m ? "bg-petal-olive text-white" : "bg-white text-petal-olive-text hover:bg-petal-beige-alt"}`}>
                {m === "login" ? "Sign In" : "Register"}
              </button>
            ))}
          </div>

          <div className="mb-6">
            <h1 className="font-playfair text-3xl text-petal-olive-text mb-1">
              {mode === "login" ? "Welcome Back" : "Create Account"}
            </h1>
            <p className="font-outfit text-sm text-petal-muted">
              {mode === "login" ? "Sign in to your account" : "Join the Petal Pookie family"}
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 font-outfit text-sm mb-6" data-testid="auth-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="block font-outfit text-xs uppercase tracking-widest text-petal-olive-text mb-1.5">Full Name *</label>
                <input type="text" value={form.name} onChange={update("name")} required placeholder="Your full name"
                  data-testid="register-name"
                  className="w-full px-4 py-3 border border-petal-border bg-petal-beige-alt font-outfit text-sm focus:outline-none focus:border-petal-olive" />
              </div>
            )}
            <div>
              <label className="block font-outfit text-xs uppercase tracking-widest text-petal-olive-text mb-1.5">Email Address *</label>
              <input type="email" value={form.email} onChange={update("email")} required placeholder="your@email.com"
                data-testid="auth-email"
                className="w-full px-4 py-3 border border-petal-border bg-petal-beige-alt font-outfit text-sm focus:outline-none focus:border-petal-olive" />
            </div>
            {mode === "register" && (
              <div>
                <label className="block font-outfit text-xs uppercase tracking-widest text-petal-olive-text mb-1.5">Phone Number</label>
                <input type="tel" value={form.phone} onChange={update("phone")} placeholder="10-digit mobile number"
                  data-testid="register-phone"
                  className="w-full px-4 py-3 border border-petal-border bg-petal-beige-alt font-outfit text-sm focus:outline-none focus:border-petal-olive" />
              </div>
            )}
            <div>
              <label className="block font-outfit text-xs uppercase tracking-widest text-petal-olive-text mb-1.5">Password *</label>
              <div className="relative">
                <input type={showPassword ? "text" : "password"} value={form.password} onChange={update("password")} required placeholder="••••••••"
                  data-testid="auth-password"
                  className="w-full px-4 py-3 pr-12 border border-petal-border bg-petal-beige-alt font-outfit text-sm focus:outline-none focus:border-petal-olive" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-petal-muted hover:text-petal-olive-text">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading} data-testid="auth-submit"
              className="w-full btn-primary py-4 disabled:opacity-60">
              {loading ? "Please wait..." : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="font-outfit text-sm text-petal-muted">
              {mode === "login" ? "Don't have an account? " : "Already have an account? "}
              <button onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
                className="text-petal-olive font-medium hover:underline" data-testid="auth-toggle">
                {mode === "login" ? "Register" : "Sign In"}
              </button>
            </p>
          </div>

          <div className="mt-6 text-center">
            <Link to="/" className="font-outfit text-xs text-petal-muted hover:text-petal-olive uppercase tracking-widest">
              ← Back to Home
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
